import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScanSession } from '@/features/scanner/ScanSessionContext';
import { checkImageQuality, type QualityIssue } from '@/services/image/qualityCheck';

const ISSUE_MESSAGES: Record<QualityIssue, string> = {
  blurry: 'The bill looks blurry.',
  too_dark: 'The photo looks too dark.',
  blank: 'This photo looks mostly blank — is the bill inside the frame?',
  low_resolution: 'This photo is quite low resolution.',
};

export default function ImagePreviewPage() {
  const navigate = useNavigate();
  const { prepared, previewUrl, reset } = useScanSession();
  const [issues, setIssues] = useState<QualityIssue[] | null>(null);

  useEffect(() => {
    if (!prepared) return;
    let cancelled = false;
    checkImageQuality(prepared.forAnalysis)
      .then((result) => {
        if (!cancelled) setIssues(result.issues);
      })
      .catch(() => {
        if (!cancelled) setIssues([]);
      });
    return () => {
      cancelled = true;
    };
  }, [prepared]);

  useEffect(() => {
    if (!prepared) navigate('/scan', { replace: true });
  }, [prepared, navigate]);

  if (!prepared || !previewUrl) return null;

  function handleRetake() {
    reset();
    navigate('/scan', { replace: true });
  }

  return (
    <div className="flex flex-col gap-5 p-4 pt-6">
      <header>
        <h1 className="text-xl font-bold text-slate-900">Review Photo</h1>
      </header>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-black">
        <img src={previewUrl} alt="Captured bill" className="max-h-[60dvh] w-full object-contain" />
      </div>

      {issues === null ? (
        <p className="text-center text-sm text-slate-500">Checking photo quality…</p>
      ) : issues.length > 0 ? (
        <div role="alert" className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">The bill is difficult to read. Please take another photo with the entire bill visible.</p>
          <ul className="mt-2 list-inside list-disc">
            {issues.map((issue) => (
              <li key={issue}>{ISSUE_MESSAGES[issue]}</li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-center text-sm text-emerald-700">This photo looks good.</p>
      )}

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => navigate('/scan/processing')}
          className="rounded-2xl bg-blue-700 px-6 py-5 text-lg font-semibold text-white shadow-sm active:bg-blue-800"
        >
          {issues && issues.length > 0 ? 'Analyze Anyway' : 'Analyze This Bill'}
        </button>
        <button
          type="button"
          onClick={handleRetake}
          className="rounded-2xl border border-slate-300 bg-white px-6 py-4 text-base font-medium text-slate-700 active:bg-slate-100"
        >
          Retake Photo
        </button>
      </div>
    </div>
  );
}
